import { useLang } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projectsData } from '@/data/projectsData';

const images = [
  'https://static.prod-images.emergentagent.com/jobs/1c1c0a12-ef9e-4115-8c24-247b6797c63a/images/a3e985a1b2c706678fb1b1514669ade3c2ea0573cdec0c003a3365d78888536f.png',
  'https://static.prod-images.emergentagent.com/jobs/1c1c0a12-ef9e-4115-8c24-247b6797c63a/images/823a26841ed3f2f20ff7b0e5e11aa364b7870b76b52a681bf50243bca753f641.png',
  'https://static.prod-images.emergentagent.com/jobs/1c1c0a12-ef9e-4115-8c24-247b6797c63a/images/bef878f503ac74634cfab2d952a0bc395518fae792059ec49b149ab916fb0062.png',
  'https://images.unsplash.com/photo-1740594967618-23cd757b9291?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwZm9vZCUyMGRhcmslMjBlbGVnYW50JTIwcmVzdGF1cmFudHxlbnwwfHx8fDE3NzYxNzA4NTV8MA&ixlib=rb-4.1.0&q=85',
];

export default function Portfolio() {
  const { t } = useLang();
  const projects = t('portfolio.projects') || [];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 lg:py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {t('portfolio.label') && t('portfolio.label') !== 'portfolio.label' && (
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4"
        >
          {t('portfolio.label')}
        </motion.p>
        )}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-5xl lg:text-6xl font-normal tracking-normal text-purple-400 mb-16"
          style={{ fontFamily: "'Great Vibes', cursive" }}
        >
          {t('portfolio.title')}
        </motion.h2>

        <div className="space-y-24 lg:space-y-32">
          {projects.map((project, i) => {
            const data = projectsData[i];
            const hasExternal = data?.externalUrl;
            const image = images[i];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8 }}
                data-testid={`portfolio-item-${i}`}
                className="group"
              >
                {/* Image - links to detail page or external */}
                {hasExternal ? (
                  <a href={data.externalUrl} target="_blank" rel="noopener noreferrer" data-testid={`portfolio-link-${i}`}>
                    <div className="overflow-hidden mb-8 relative">
                      {image ? (
                        <img
                          src={image}
                          alt={project.title}
                          className="w-full max-h-[70vh] object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 scale-[1.02] group-hover:scale-100"
                        />
                      ) : (
                        <div className="w-full h-[50vh] bg-gradient-to-br from-purple-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
                          <span className="font-outfit text-6xl lg:text-8xl font-black text-purple-500/20 tracking-tighter">2good2breal</span>
                        </div>
                      )}
                    </div>
                  </a>
                ) : (
                  <Link to={`/project/${data?.slug || ''}`} data-testid={`portfolio-link-${i}`}>
                    <div className="overflow-hidden mb-8">
                      <img
                        src={image}
                        alt={project.title}
                        className="w-full max-h-[70vh] object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 scale-[1.02] group-hover:scale-100"
                      />
                    </div>
                  </Link>
                )}

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                      {project.category}
                    </p>
                    {hasExternal ? (
                      <a href={data.externalUrl} target="_blank" rel="noopener noreferrer">
                        <h3 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-purple-400 flex items-center gap-3 hover:text-purple-300 transition-colors">
                          {project.title}
                          <ExternalLink size={22} className="text-zinc-600 group-hover:text-zinc-50 transition-all duration-300" />
                        </h3>
                      </a>
                    ) : (
                      <Link to={`/project/${data?.slug || ''}`}>
                        <h3 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight text-purple-400 flex items-center gap-3 hover:text-purple-300 transition-colors">
                          {project.title}
                          <ArrowUpRight size={24} className="text-zinc-600 group-hover:text-zinc-50 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                        </h3>
                      </Link>
                    )}
                    {project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`portfolio-url-${i}`}
                        className="text-sm font-manrope text-zinc-500 hover:text-zinc-300 transition-colors mt-1 inline-block"
                      >
                        {project.url.replace('https://', '')}
                      </a>
                    )}
                  </div>
                  <p className="text-base font-manrope text-zinc-400 max-w-md leading-relaxed">
                    {project.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
